import { Link } from "react-router-dom";
import SEOMeta from "@/components/SEOMeta";
import { Skeleton } from "@/components/ui/skeleton";
import { useBioProfile } from "@/hooks/useBioProfile";
import { usePublicBioLinks, type BioLink } from "@/hooks/useBioLinks";
import { useTrackEvent } from "@/hooks/useAnalytics";
import { BioView } from "@/components/bio/BioView";
import { LoadErrorState } from "@/components/shared/LoadErrorState";

const Bio = () => {
  const {
    data: profile,
    isLoading: profileLoading,
    isError: profileError,
    refetch: refetchProfile,
  } = useBioProfile();
  const {
    data: links,
    isLoading: linksLoading,
    isError: linksError,
    refetch: refetchLinks,
  } = usePublicBioLinks();
  const trackEvent = useTrackEvent();

  const isLoading = profileLoading || linksLoading;

  // A failed fetch is not "unpublished": offer a retry instead.
  if (profileError || linksError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="rounded-2xl border border-border bg-card shadow-card">
          <LoadErrorState
            title="Não foi possível carregar a página."
            onRetry={() => {
              refetchProfile();
              refetchLinks();
            }}
          />
        </div>
      </div>
    );
  }

  if (!isLoading && (!profile || !profile.is_published)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="space-y-3 rounded-2xl border border-border bg-card p-8 text-center shadow-card">
          <p className="text-lg font-medium text-foreground">
            Esta página não está disponível no momento.
          </p>
          <Link to="/" className="text-sm text-primary underline hover:no-underline">
            Voltar ao site
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading || !profile) {
    return (
      <div className="flex min-h-screen flex-col items-center gap-4 bg-background p-6 pt-16">
        <Skeleton className="h-24 w-24 rounded-full" />
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-60" />
        <div className="mt-6 w-full max-w-md space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const trackClick = (link: BioLink) => {
    trackEvent.mutate({
      event_type: "link_click",
      page_path: "/bio",
      metadata: {
        link_id: link.id,
        kind: link.kind,
        url: link.url ?? undefined,
        title: link.title ?? undefined,
      },
    });
  };

  return (
    <>
      <SEOMeta
        title={profile.display_name}
        description={profile.tagline || `Links de ${profile.display_name}`}
        canonicalUrl="/bio"
        ogImage={profile.avatar_url || undefined}
      />
      <BioView profile={profile} links={links || []} onLinkClick={trackClick} />
    </>
  );
};

export default Bio;
